import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  CloudSyncOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import { PlayQuestionService } from "./PlayQuestionService";
import { PlayQuestion, PlayQuestionFormValues, PlayQuestionType } from "./web_parts/types";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Setting_PlayQuestionsProps {
  questionType: PlayQuestionType;
}

const Setting_PlayQuestions: React.FC<Setting_PlayQuestionsProps> = ({ questionType }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [items, setItems] = useState<PlayQuestion[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selected, setSelected] = useState<PlayQuestion | null>(null);
  const [form] = Form.useForm<PlayQuestionFormValues>();

  const isQuiz = questionType === "quiz";
  const label = isQuiz ? "3択クイズ" : "なぞなぞ";

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const list = await PlayQuestionService.getByType(questionType);
      setItems(list);
    } catch (e) {
      console.error(e);
      message.error(`${label}の読み込みに失敗しました`);
    } finally {
      setLoading(false);
    }
  }, [questionType, label]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const notifyDevices = useCallback(async (): Promise<number> => {
    const snapshot = await getDocs(collection(db, "devices"));
    const tokens = snapshot.docs
      .map((d) => d.data())
      .filter((data) => data.isDeleted !== true)
      .map((data) => (typeof data.fcmToken === "string" ? data.fcmToken.trim() : ""))
      .filter((token) => token.length > 0);

    if (tokens.length === 0) {
      return 0;
    }

    const payload = {
      mode: "request",
      command: "update_play_questions",
      user: currentUser?.displayName || currentUser?.email || "admin",
      place: "WEB",
      param1: String(Date.now()),
    };

    await axios.post(
      "/api/sendFCM",
      { tokens, payload },
      { headers: { "Content-Type": "application/json" } }
    );
    return tokens.length;
  }, [currentUser]);

  const handleNotifyDevices = async () => {
    setSyncing(true);
    try {
      const count = await notifyDevices();
      if (count === 0) {
        message.warning("通知可能な端末（FCMトークン付き）が見つかりませんでした");
      } else {
        message.success(`${count} 台の端末へ同期通知を送信しました`);
      }
    } catch (e) {
      console.error(e);
      message.error("端末への同期通知に失敗しました");
    } finally {
      setSyncing(false);
    }
  };

  const openModal = (mode: "create" | "edit", item?: PlayQuestion) => {
    form.resetFields();
    setModalMode(mode);
    if (mode === "edit" && item) {
      setSelected(item);
      form.setFieldsValue({
        id: item.id,
        question: item.question,
        answer: item.answer,
        hint: item.hint,
        choice1: item.choice1,
        choice2: item.choice2,
        choice3: item.choice3,
        correctIndex: item.correctIndex || 1,
        genre: item.genre,
        enabled: item.enabled,
        sortOrder: item.sortOrder,
      });
    } else {
      setSelected(null);
      form.setFieldsValue({
        id: undefined,
        question: "",
        answer: "",
        hint: "",
        choice1: "",
        choice2: "",
        choice3: "",
        correctIndex: 1,
        genre: "",
        enabled: true,
        sortOrder: PlayQuestionService.nextSortOrder(items),
      });
    }
    setIsModalVisible(true);
  };

  const handleSubmit = async (values: PlayQuestionFormValues) => {
    setLoading(true);
    try {
      if (modalMode === "create") {
        await PlayQuestionService.create(questionType, values);
        message.success(`${label}を登録しました`);
      } else if (selected) {
        await PlayQuestionService.update(selected.id, questionType, values);
        message.success(`${label}を更新しました`);
      }
      setIsModalVisible(false);
      await loadItems();
      try {
        const count = await notifyDevices();
        if (count > 0) {
          message.info(`${count} 台の端末へ同期通知を送信しました`);
        }
      } catch (fcmError) {
        console.error(fcmError);
        message.warning("保存は完了しましたが、端末への同期通知に失敗しました");
      }
    } catch (e: unknown) {
      console.error(e);
      const errMsg = e instanceof Error ? e.message : `${label}の保存に失敗しました`;
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: PlayQuestion) => {
    setLoading(true);
    try {
      await PlayQuestionService.remove(item.id);
      message.success(`${label}を削除しました`);
      await loadItems();
      try {
        await notifyDevices();
      } catch (fcmError) {
        console.error(fcmError);
      }
    } catch (e) {
      console.error(e);
      message.error(`${label}の削除に失敗しました`);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<PlayQuestion> = [
    {
      title: "並び",
      dataIndex: "sortOrder",
      key: "sortOrder",
      width: 70,
    },
    {
      title: "問題",
      dataIndex: "question",
      key: "question",
      ellipsis: true,
    },
    ...(isQuiz
      ? ([
          {
            title: "ジャンル",
            dataIndex: "genre",
            key: "genre",
            width: 100,
          },
          {
            title: "正解",
            key: "correct",
            width: 140,
            render: (_: unknown, r: PlayQuestion) => {
              const choices = [r.choice1, r.choice2, r.choice3];
              const idx = r.correctIndex;
              const text = idx >= 1 && idx <= 3 ? choices[idx - 1] : r.answer;
              return text || r.answer;
            },
          },
        ] as ColumnsType<PlayQuestion>)
      : ([
          {
            title: "答え",
            dataIndex: "answer",
            key: "answer",
            width: 160,
            ellipsis: true,
          },
        ] as ColumnsType<PlayQuestion>)),
    {
      title: "有効",
      dataIndex: "enabled",
      key: "enabled",
      width: 80,
      render: (v: boolean) =>
        v ? <Tag color="green">有効</Tag> : <Tag>無効</Tag>,
    },
    {
      title: "操作",
      key: "action",
      width: 120,
      render: (_, r) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openModal("edit", r)} />
          <Popconfirm
            title={`${label}を削除しますか？`}
            description="Firestore の play_questions から削除されます。"
            onConfirm={() => void handleDelete(r)}
            okText="削除"
            cancelText="キャンセル"
            okType="danger"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Firestore の play_questions（type={questionType}）を管理します。保存後、端末へ同期通知を送ります。
      </Text>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal("create")}>
          {label}を追加
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => void loadItems()} loading={loading}>
          更新
        </Button>
        <Button
          icon={<CloudSyncOutlined />}
          onClick={() => void handleNotifyDevices()}
          loading={syncing}
        >
          端末へ同期通知
        </Button>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="middle"
      />

      <Modal
        title={modalMode === "create" ? `${label}の登録` : `${label}の編集`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => void handleSubmit(v)}
        >
          {modalMode === "edit" ? (
            <Form.Item label="ID">
              <Input value={selected?.id} disabled />
            </Form.Item>
          ) : (
            <Form.Item
              label="ID（任意）"
              name="id"
              extra="空欄の場合は自動採番します"
            >
              <Input placeholder={`${questionType}_...`} />
            </Form.Item>
          )}

          <Form.Item
            label="問題文"
            name="question"
            rules={[{ required: true, message: "問題文を入力してください" }]}
          >
            <TextArea rows={3} />
          </Form.Item>

          {isQuiz ? (
            <>
              <Form.Item
                label="選択肢1"
                name="choice1"
                rules={[{ required: true, message: "選択肢1を入力してください" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="選択肢2"
                name="choice2"
                rules={[{ required: true, message: "選択肢2を入力してください" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="選択肢3"
                name="choice3"
                rules={[{ required: true, message: "選択肢3を入力してください" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="正解の番号"
                name="correctIndex"
                rules={[{ required: true, message: "正解番号を選択してください" }]}
              >
                <Select>
                  <Option value={1}>1</Option>
                  <Option value={2}>2</Option>
                  <Option value={3}>3</Option>
                </Select>
              </Form.Item>
              <Form.Item
                label="正解テキスト（任意）"
                name="answer"
                extra="空欄の場合は選択した選択肢の文言を自動設定します"
              >
                <Input />
              </Form.Item>
              <Form.Item label="ジャンル" name="genre">
                <Input placeholder="例: 歴史、地理、科学" />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              label="答え"
              name="answer"
              rules={[{ required: true, message: "答えを入力してください" }]}
            >
              <Input />
            </Form.Item>
          )}

          <Form.Item label="ヒント" name="hint">
            <Input />
          </Form.Item>
          <Form.Item label="並び順" name="sortOrder" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="有効" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>

          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>キャンセル</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Setting_PlayQuestions;
